import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, retry, throwError, timer } from 'rxjs';
import { LoggerService } from 'src/app/shared/services/logger.service';
import { SKIP_RETRY } from 'src/app/core/http/http-context.tokens';

/** Attempts after the first. Total requests on a persistently failing call = 3. */
export const MAX_RETRIES = 2;

const BACKOFF_STEP_MS = 800;
const JITTER_MS = 250;

/**
 * Only methods with no server-side effect are retried. This alone protects every
 * money-moving call in the app — including the deliberate 2-attempt retry around
 * OnboardingFeeService.recordPayment, which is a POST.
 */
// QUERY is a read like GET (the item list filters travel in its body), so it is retried the same way.
const RETRYABLE_METHODS = new Set(['GET', 'HEAD', 'QUERY']);

/**
 * 0   — transport failure, nothing reached the server
 * 408 — emitted by TimeoutInterceptor when a socket stalls
 * 502 — bad gateway, the origin was never reached
 * 503 — origin explicitly refusing work right now
 *
 * 504 is deliberately absent. A gateway timeout means the origin already spent its
 * full budget doing the work; the marketplace listing endpoint runs 3 queries per
 * returned NFT, so retrying a 504 triples load on the one endpoint already collapsing.
 */
const RETRYABLE_STATUSES = new Set([0, 408, 502, 503]);

/**
 * GETs that mutate server state. A method allowlist is NOT sufficient here — these
 * delete notifications, send mail, and burn single-use signing nonces, so retrying
 * them would double-delete or invalidate a signature the user is about to submit.
 */
const SIDE_EFFECTING_GET_PATTERN =
  /\/delete-notification|\/send-mail|\/resend-email|\/transaction-fail-notify|\/nonce|\/signature-nonce|\/transak\/create-widget-url|\/idenfy\//;

@Injectable()
export class RetryInterceptor implements HttpInterceptor {

  constructor(private readonly logger: LoggerService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isRetryable(req)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      retry({
        count: MAX_RETRIES,
        delay: (error: unknown, retryCount: number) => {
          const status = Number((error as HttpErrorResponse)?.status ?? 0);

          if (!RETRYABLE_STATUSES.has(status)) {
            return throwError(() => error);
          }

          // Jitter keeps a fleet of clients recovering from one outage from
          // resynchronising into a thundering herd.
          const wait = retryCount * BACKOFF_STEP_MS + Math.round(Math.random() * JITTER_MS);
          this.logger.debug('Retrying HTTP request', {
            url: req.url,
            method: req.method,
            status,
            attempt: retryCount + 1,
            waitMs: wait
          });

          return timer(wait);
        }
      })
    );
  }

  private isRetryable(req: HttpRequest<unknown>): boolean {
    if (req.context.get(SKIP_RETRY)) {
      return false;
    }
    if (!RETRYABLE_METHODS.has(req.method.toUpperCase())) {
      return false;
    }
    return !SIDE_EFFECTING_GET_PATTERN.test(req.url);
  }
}
